import axios, { AxiosError } from "axios";
import { catchErrorLog } from "@util/sentry";
import { getCookie } from "@util/cookie";
import type {
    DashboardStats,
    DashboardChartResponse,
    CallSession,
    CallSessionListResponse,
    CallSessionFilters,
    CampaignListItem,
    Campaign,
    VoiceProviderSettings,
    CampaignStatus,
    EvaluationCriterion,
    AutomationRules,
    CampaignContact,
    ActivityLogEntry,
    FlowQuestion,
    TranscriptEntry,
    ScoreBreakdown,
    InitiateCallResponse,
    KnowledgeDocument,
    UseCaseType,
    TriggerMethod,
    ConversationMode,
    ProbingDepth,
    ContactStatus,
    SessionStatus,
    SentimentLevel,
    CallCategory,
    ActivityEventType,
} from "@type/agent";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

// ============================================
// RAW API RESPONSE TYPES (snake_case from backend)
// ============================================

interface RawDashboardChartPoint {
    date: string;
    total_calls: number;
    success_calls: number;
    failed_calls: number;
}

interface RawKnowledgeDocument {
    id: string;
    file_name: string;
    file_size: number;
    uploaded_at: string;
}

interface RawEvaluationCriterion {
    id: string;
    name: string;
    description?: string;
    weight: number;
    enabled: boolean;
    order?: number;
}

interface RawActivityLogEntry {
    id: string;
    campaign_id?: string;
    event_type: ActivityEventType;
    message: string;
    details?: string;
    contact_phone?: string;
    contact_name?: string;
    session_id?: string;
    timestamp: string;
}

interface RawCampaignListItem {
    id: string;
    name: string;
    agent_name?: string;
    use_case: UseCaseType;
    status: CampaignStatus;
    language: string;
    trigger_method: TriggerMethod;
    contact_count?: number;
    session_count?: number;
    success_rate?: number;
    avg_duration_seconds?: number;
    created_at: string;
    updated_at: string;
}

interface RawCampaignDetail {
    id: string;
    name: string;
    agent_name?: string;
    use_case: UseCaseType;
    status: CampaignStatus;
    language: string;
    timezone: string;
    max_duration_seconds: number;
    opening_script?: string;
    closing_script?: string;
    core_questions?: FlowQuestion[];
    conditional_logic?: Record<string, unknown>;
    trigger_method: TriggerMethod;
    retry_attempts: number;
    retry_delay_minutes: number;
    webhook_url?: string;
    api_endpoint?: string;
    schedule_config?: Record<string, unknown>;
    voice_agent_id?: string;
    voice_id?: string;
    conversation_mode?: ConversationMode;
    objective?: string;
    knowledge_documents?: RawKnowledgeDocument[];
    conversation_goal?: string;
    topic_areas?: string[];
    probing_depth?: ProbingDepth;
    guardrails?: string;
    evaluation_criteria?: RawEvaluationCriterion[];
    contact_count?: number;
    session_count?: number;
    recent_activity?: RawActivityLogEntry[];
    created_at: string;
    updated_at: string;
    activated_at?: string;
}

interface RawContact {
    id: string;
    phone_number: string;
    name?: string;
    merchant_name?: string;
    extra_data?: Record<string, string>;
    status: ContactStatus;
    added_at: string;
    called_at?: string;
}

interface RawTranscriptEntry {
    id?: string;
    speaker?: "agent" | "user";
    role?: string;
    content?: string;
    text?: string;
    timestamp_secs?: number;
    timestamp?: number;
    order?: number;
}

interface RawScoreBreakdown {
    criterion: string;
    score: number;
    max_score: number;
    weight: number;
    notes?: string;
}

interface RawCallSession {
    id: string;
    contact_name: string;
    contact_phone: string;
    campaign_id?: string;
    campaign_name?: string;
    batch_call_id?: string;
    is_test?: boolean;
    status: SessionStatus;
    duration: number;
    score?: number;
    sentiment?: SentimentLevel;
    category?: CallCategory;
    created_at: string;
    started_at: string;
    ended_at?: string;
    scheduled_at?: string;
    summary?: string;
    transcript?: RawTranscriptEntry[];
    messages?: RawTranscriptEntry[];
    score_breakdowns?: RawScoreBreakdown[];
    key_findings?: string[];
    risk_signals?: string[];
    next_actions?: string[];
    audio_url?: string;
    message_count?: number;
    retry_count?: number;
    fail_reason?: string;
    fail_code?: string;
    max_retry_attempts?: number;
}

interface RawInitiateCallSession {
    session_id: string;
    conversation_id?: string;
    contact_phone: string;
}

interface RawInitiateCallResponse {
    type: "single" | "batch";
    batch_call_id?: string;
    sessions?: RawInitiateCallSession[];
    skipped_count?: number;
}

function getHeaders() {
    const token = getCookie("authToken");
    return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
    };
}

// ============================================
// DASHBOARD
// ============================================

export async function getAgentDashboardStats(dateRange?: string): Promise<DashboardStats> {
    const response = await axios({
        url: `${apiURL}/sara/dashboard/stats`,
        method: "GET",
        headers: getHeaders(),
        params: dateRange && dateRange !== "all" ? { range: dateRange } : undefined,
        timeout: 10000,
    });
    const d = response.data.data;
    return {
        totalCampaigns: d.total_campaigns,
        activeCampaigns: d.active_campaigns,
        totalCalls: d.total_calls,
        completedCalls: d.completed_calls,
        avgDurationSeconds: d.avg_duration_seconds,
        avgScore: d.avg_score,
        totalContacts: d.total_contacts,
    };
}

export async function getAgentDashboardChart(dateRange?: string): Promise<DashboardChartResponse> {
    try {
        const response = await axios({
            url: `${apiURL}/sara/dashboard/chart`,
            method: "GET",
            headers: getHeaders(),
            params: dateRange && dateRange !== "all" ? { range: dateRange } : undefined,
            timeout: 10000,
        });
        const d = response.data.data;
        return {
            dataPoints: (d.data_points || []).map((p: RawDashboardChartPoint) => ({
                date: p.date,
                totalCalls: p.total_calls,
                successCalls: p.success_calls,
                failedCalls: p.failed_calls,
            })),
            overallSuccessRate: d.overall_success_rate ?? 0,
        };
    } catch (error) {
        catchErrorLog(`failed to fetch agent dashboard chart, err: ${(error as AxiosError).message}`);
        return { dataPoints: [], overallSuccessRate: 0 };
    }
}

// ============================================
// CAMPAIGNS
// ============================================

export async function getAgentCampaigns(
    status?: string
): Promise<CampaignListItem[]> {
    const response = await axios({
        url: `${apiURL}/sara/campaigns`,
        method: "GET",
        headers: getHeaders(),
        params: status ? { status } : undefined,
        timeout: 15000,
    });
    return (response.data.data as RawCampaignListItem[]).map(mapCampaignListItem);
}

export async function getAgentCampaign(
    id: string
): Promise<Campaign | null> {
    try {
        const response = await axios({
            url: `${apiURL}/sara/campaigns/${id}`,
            method: "GET",
            headers: getHeaders(),
            timeout: 15000,
        });
        return mapCampaignDetail(response.data.data);
    } catch (error) {
        const err = error as AxiosError;
        if (err.response?.status === 404) return null;
        throw error;
    }
}

export async function createAgentCampaign(
    campaign: Partial<Campaign>
): Promise<{ id: string; voiceAgentId?: string }> {
    const response = await axios({
        url: `${apiURL}/sara/campaigns`,
        method: "POST",
        headers: getHeaders(),
        data: {
            name: campaign.name,
            agent_name: campaign.agentName || null,
            use_case: campaign.useCase || null,
            language: campaign.language || "en",
            timezone: campaign.timezone || "UTC",
            max_duration_seconds: campaign.maxDurationSeconds || 300,
            opening_script: campaign.openingScript || null,
            closing_script: campaign.closingScript || null,
            core_questions: campaign.coreQuestions || [],
            conditional_logic: campaign.conditionalLogic || {},
            trigger_method: campaign.triggerMethod || "manual",
            retry_attempts: campaign.retryAttempts || 0,
            retry_delay_minutes: campaign.retryDelayMinutes || 5,
            webhook_url: campaign.webhookUrl || null,
            api_endpoint: campaign.apiEndpoint || null,
            schedule_config: campaign.scheduleConfig || {},
            objective: campaign.objective || null,
            conversation_mode: campaign.conversationMode || "scripted",
            conversation_goal: campaign.conversationGoal || null,
            topic_areas: campaign.topicAreas || [],
            probing_depth: campaign.probingDepth || "medium",
            guardrails: campaign.guardrails || null,
            voice_id: campaign.voiceId || null,
        },
        timeout: 15000,
    });
    const d = response.data.data;
    return { id: d.id, voiceAgentId: d.voice_agent_id };
}

export async function updateAgentCampaign(
    id: string,
    updates: Partial<Campaign>
): Promise<void> {
    await axios({
        url: `${apiURL}/sara/campaigns/${id}`,
        method: "PUT",
        headers: getHeaders(),
        data: {
            name: updates.name,
            agent_name: updates.agentName,
            use_case: updates.useCase,
            language: updates.language,
            timezone: updates.timezone,
            max_duration_seconds: updates.maxDurationSeconds,
            voice_id: updates.voiceId,
        },
        timeout: 15000,
    });
}

export async function updateCampaignStatus(
    id: string,
    status: CampaignStatus
): Promise<void> {
    await axios({
        url: `${apiURL}/sara/campaigns/${id}/status`,
        method: "PATCH",
        headers: getHeaders(),
        data: { status },
        timeout: 10000,
    });
}

export async function deleteAgentCampaign(id: string): Promise<void> {
    await axios({
        url: `${apiURL}/sara/campaigns/${id}`,
        method: "DELETE",
        headers: getHeaders(),
        timeout: 10000,
    });
}

// ============================================
// CAMPAIGN SUB-RESOURCES
// ============================================

export async function updateCampaignFlow(
    campaignId: string,
    flow: {
        openingScript?: string;
        closingScript?: string;
        coreQuestions?: FlowQuestion[];
        conditionalLogic?: Record<string, unknown>;
        conversationMode?: string;
        objective?: string;
        conversationGoal?: string;
        topicAreas?: string[];
        probingDepth?: string;
        guardrails?: string;
    }
): Promise<void> {
    await axios({
        url: `${apiURL}/sara/campaigns/${campaignId}/flow`,
        method: "PUT",
        headers: getHeaders(),
        data: {
            opening_script: flow.openingScript,
            closing_script: flow.closingScript,
            core_questions: flow.coreQuestions,
            conditional_logic: flow.conditionalLogic,
            conversation_mode: flow.conversationMode,
            objective: flow.objective,
            conversation_goal: flow.conversationGoal,
            topic_areas: flow.topicAreas,
            probing_depth: flow.probingDepth,
            guardrails: flow.guardrails,
        },
        timeout: 10000,
    });
}

export async function updateCampaignEvaluation(
    campaignId: string,
    criteria: EvaluationCriterion[]
): Promise<void> {
    await axios({
        url: `${apiURL}/sara/campaigns/${campaignId}/evaluation`,
        method: "PUT",
        headers: getHeaders(),
        data: {
            criteria: criteria.map((c) => ({
                name: c.name,
                description: c.description,
                weight: c.weight,
                enabled: c.enabled,
                order: c.order || 0,
            })),
        },
        timeout: 10000,
    });
}

export async function updateCampaignAutomation(
    campaignId: string,
    rules: Partial<AutomationRules>
): Promise<void> {
    await axios({
        url: `${apiURL}/sara/campaigns/${campaignId}/automation`,
        method: "PUT",
        headers: getHeaders(),
        data: {
            trigger_method: rules.triggerMethod,
            retry_attempts: rules.retryAttempts,
            retry_delay_minutes: rules.retryDelayMinutes,
            webhook_url: rules.webhookUrl,
            api_endpoint: rules.apiEndpoint,
            schedule_config: rules.scheduleConfig,
        },
        timeout: 10000,
    });
}

// ============================================
// CONTACTS
// ============================================

export async function getCampaignContacts(
    campaignId: string,
    status?: string,
    page?: number,
    limit?: number
): Promise<{ contacts: CampaignContact[]; total: number }> {
    const response = await axios({
        url: `${apiURL}/sara/campaigns/${campaignId}/contacts`,
        method: "GET",
        headers: getHeaders(),
        params: {
            status: status || undefined,
            page: page || 1,
            limit: limit || 50,
        },
        timeout: 15000,
    });
    const items = response.data.data as RawContact[];
    const pagination = response.data.meta?.pagination;
    return {
        contacts: items.map(mapContact),
        total: pagination?.total || items.length,
    };
}

export async function addCampaignContacts(
    campaignId: string,
    phoneNumbers: string[],
    name?: string,
    merchantName?: string,
    extraData?: Record<string, string>
): Promise<{ added: number; skipped: number }> {
    const response = await axios({
        url: `${apiURL}/sara/campaigns/${campaignId}/contacts`,
        method: "POST",
        headers: getHeaders(),
        data: {
            phone_numbers: phoneNumbers,
            name: name || undefined,
            merchant_name: merchantName || undefined,
            extra_data: extraData || undefined,
        },
        timeout: 15000,
    });
    return response.data.data;
}

export async function uploadCampaignContacts(
    campaignId: string,
    file: File
): Promise<{ added: number; skipped: number }> {
    const formData = new FormData();
    formData.append("file", file);
    const token = getCookie("authToken");
    const response = await axios({
        url: `${apiURL}/sara/campaigns/${campaignId}/contacts/upload`,
        method: "POST",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        data: formData,
        timeout: 30000,
    });
    return response.data.data;
}

export async function updateCampaignContact(
    campaignId: string,
    contactId: string,
    updates: { name?: string; merchantName?: string; status?: string; extraData?: Record<string, string> }
): Promise<void> {
    await axios({
        url: `${apiURL}/sara/campaigns/${campaignId}/contacts/${contactId}`,
        method: "PATCH",
        headers: getHeaders(),
        data: {
            name: updates.name,
            merchant_name: updates.merchantName,
            status: updates.status,
            extra_data: updates.extraData,
        },
        timeout: 10000,
    });
}

export async function deleteCampaignContact(
    campaignId: string,
    contactId: string
): Promise<void> {
    await axios({
        url: `${apiURL}/sara/campaigns/${campaignId}/contacts/${contactId}`,
        method: "DELETE",
        headers: getHeaders(),
        timeout: 10000,
    });
}

// ============================================
// OUTBOUND CALL
// ============================================

function mapInitiateCallResponse(d: RawInitiateCallResponse): InitiateCallResponse {
    return {
        type: d.type,
        batchCallId: d.batch_call_id,
        sessions: (d.sessions || []).map((s: RawInitiateCallSession) => ({
            sessionId: s.session_id,
            conversationId: s.conversation_id,
            contactPhone: s.contact_phone,
        })),
        skippedCount: d.skipped_count || 0,
    };
}

async function postCampaignCall(campaignId: string, data: Record<string, unknown>): Promise<InitiateCallResponse> {
    const response = await axios({
        url: `${apiURL}/sara/campaigns/${campaignId}/call`,
        method: "POST",
        headers: getHeaders(),
        data,
        timeout: 30000,
    });
    return mapInitiateCallResponse(response.data.data);
}

export async function initiateTestCall(
    campaignId: string,
    phoneNumber: string,
    name?: string,
    merchantName?: string,
): Promise<InitiateCallResponse> {
    const data: Record<string, unknown> = { mode: "test", phone_number: phoneNumber };
    if (name) data.name = name;
    if (merchantName) data.merchant_name = merchantName;
    return postCampaignCall(campaignId, data);
}

export async function initiateImmediateCall(
    campaignId: string,
    contactIds: string[],
): Promise<InitiateCallResponse> {
    return postCampaignCall(campaignId, { mode: "immediate", contact_ids: contactIds });
}

export async function initiateScheduledCall(
    campaignId: string,
    contactIds: string[],
    scheduledDate: string,
    scheduledTime: string,
): Promise<InitiateCallResponse> {
    return postCampaignCall(campaignId, {
        mode: "scheduled",
        contact_ids: contactIds,
        scheduled_date: scheduledDate,
        scheduled_time: scheduledTime,
    });
}

// ============================================
// CALL SESSIONS
// ============================================

export async function getCallCategories(): Promise<string[]> {
    const response = await axios({
        url: `${apiURL}/sara/call-sessions/categories`,
        method: "GET",
        headers: getHeaders(),
        timeout: 10000,
    });
    return response.data.data;
}

export async function getAgentCallSessions(
    filters?: CallSessionFilters
): Promise<CallSessionListResponse> {
    const response = await axios({
        url: `${apiURL}/sara/call-sessions`,
        method: "GET",
        headers: getHeaders(),
        params: {
            search: filters?.search || undefined,
            status: filters?.status || undefined,
            campaign_id: filters?.campaignId || undefined,
            category: filters?.category || undefined,
            date_after: filters?.dateAfter || undefined,
            date_before: filters?.dateBefore || undefined,
            page: filters?.page || 1,
            limit: filters?.limit || 50,
        },
        timeout: 15000,
    });
    const items = response.data.data as RawCallSession[];
    const pagination = response.data.meta?.pagination;
    return {
        sessions: items.map(mapCallSessionListItem),
        total: pagination?.total || items.length,
        page: pagination?.page || 1,
        limit: pagination?.limit || 50,
    };
}

export async function getAgentCallSession(
    id: string,
    refresh?: boolean
): Promise<CallSession | null> {
    try {
        const response = await axios({
            url: `${apiURL}/sara/call-sessions/${id}`,
            method: "GET",
            headers: getHeaders(),
            params: refresh ? { refresh: true } : undefined,
            timeout: 15000,
        });
        return mapCallSessionDetail(response.data.data);
    } catch (error) {
        const err = error as AxiosError;
        if (err.response?.status === 404) return null;
        throw error;
    }
}

export async function analyzeCallSession(
    sessionId: string
): Promise<{
    summary: string;
    keyFindings: string[];
    riskSignals: string[];
    nextActions: string[];
    sentiment: string;
    score: number;
}> {
    const response = await axios({
        url: `${apiURL}/sara/call-sessions/${sessionId}/analyze`,
        method: "POST",
        headers: getHeaders(),
        timeout: 60000,
    });
    const d = response.data.data;
    return {
        summary: d.summary,
        keyFindings: d.key_findings,
        riskSignals: d.risk_signals,
        nextActions: d.next_actions,
        sentiment: d.sentiment,
        score: d.score,
    };
}

export async function updateSessionCategory(
    sessionId: string,
    category: string
): Promise<void> {
    await axios({
        url: `${apiURL}/sara/call-sessions/${sessionId}/category`,
        method: "PATCH",
        headers: getHeaders(),
        data: { category },
        timeout: 10000,
    });
}

export async function getSessionAudioUrl(
    sessionId: string
): Promise<string | null> {
    try {
        const token = getCookie("authToken");
        const response = await axios({
            url: `${apiURL}/sara/call-sessions/${sessionId}/audio`,
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            responseType: "blob",
            timeout: 30000,
        });
        const blob = new Blob([response.data], { type: "audio/mpeg" });
        return URL.createObjectURL(blob);
    } catch (error) {
        catchErrorLog(`failed to fetch call audio, err: ${(error as AxiosError).message}`);
        return null;
    }
}

// ============================================
// ACTIVITY LOG
// ============================================

export async function getCampaignActivityLog(
    campaignId: string,
    filter?: string,
    page?: number,
    limit?: number
): Promise<{ entries: ActivityLogEntry[]; total: number }> {
    const response = await axios({
        url: `${apiURL}/sara/campaigns/${campaignId}/activity`,
        method: "GET",
        headers: getHeaders(),
        params: {
            filter: filter || undefined,
            page: page || 1,
            limit: limit || 50,
        },
        timeout: 10000,
    });
    const items = response.data.data as RawActivityLogEntry[];
    const pagination = response.data.meta?.pagination;
    return {
        entries: items.map(mapActivityLogEntry),
        total: pagination?.total || items.length,
    };
}

// ============================================
// SETTINGS — VOICE PROVIDER
// ============================================

export async function getVoiceProviderSettings(): Promise<VoiceProviderSettings> {
    const response = await axios({
        url: `${apiURL}/sara/settings/voice-provider`,
        method: "GET",
        headers: getHeaders(),
        timeout: 10000,
    });
    const d = response.data.data;
    return {
        phoneNumberId: d.phone_number_id || "",
        provider: d.provider || "elevenlabs",
        config: d.config || {},
    };
}

export async function updateVoiceProvider(
    data: { phoneNumberId: string; provider?: string; config?: Record<string, unknown> }
): Promise<void> {
    await axios({
        url: `${apiURL}/sara/settings/voice-provider`,
        method: "PUT",
        headers: getHeaders(),
        data: {
            phone_number_id: data.phoneNumberId,
            provider: data.provider || "elevenlabs",
            config: data.config || {},
        },
        timeout: 10000,
    });
}

export async function syncVoiceProvider(): Promise<{ connected: boolean; agentCount?: number }> {
    const response = await axios({
        url: `${apiURL}/sara/settings/voice-provider/sync`,
        method: "POST",
        headers: getHeaders(),
        timeout: 15000,
    });
    return response.data.data;
}

// ============================================
// CAMPAIGN DOCUMENTS (KNOWLEDGE BASE)
// ============================================

export async function uploadCampaignDocument(
    campaignId: string,
    file: File
): Promise<KnowledgeDocument> {
    const formData = new FormData();
    formData.append("file", file);
    const token = getCookie("authToken");
    const response = await axios({
        url: `${apiURL}/sara/campaigns/${campaignId}/documents`,
        method: "POST",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        data: formData,
        timeout: 60000,
    });
    const d = response.data.data;
    return {
        id: d.id,
        fileName: d.file_name,
        fileSize: d.file_size,
        uploadedAt: d.uploaded_at,
    };
}

export async function deleteCampaignDocument(
    campaignId: string,
    documentId: string
): Promise<void> {
    await axios({
        url: `${apiURL}/sara/campaigns/${campaignId}/documents/${documentId}`,
        method: "DELETE",
        headers: getHeaders(),
        timeout: 10000,
    });
}

// ============================================
// AGENT PREFERENCES (VOICE & LANGUAGE)
// ============================================

export async function getAgentPreferences(): Promise<{ voiceId: string; language: string }> {
    const response = await axios({
        url: `${apiURL}/sara/settings/preferences`,
        method: "GET",
        headers: getHeaders(),
        timeout: 10000,
    });
    const d = response.data.data;
    return {
        voiceId: d.voice_id || "en-west-f",
        language: d.language || "en-west",
    };
}

export async function updateAgentPreferences(prefs: { voiceId: string; language: string }): Promise<void> {
    await axios({
        url: `${apiURL}/sara/settings/preferences`,
        method: "PUT",
        headers: getHeaders(),
        data: {
            voice_id: prefs.voiceId,
            language: prefs.language,
        },
        timeout: 10000,
    });
}

// ============================================
// MAPPERS (snake_case API → camelCase frontend)
// ============================================

function mapCampaignListItem(raw: RawCampaignListItem): CampaignListItem {
    return {
        id: raw.id,
        name: raw.name,
        agentName: raw.agent_name,
        useCase: raw.use_case,
        status: raw.status,
        language: raw.language,
        triggerMethod: raw.trigger_method,
        contactCount: raw.contact_count || 0,
        sessionCount: raw.session_count || 0,
        successRate: raw.success_rate ?? undefined,
        avgDurationSeconds: raw.avg_duration_seconds ?? undefined,
        createdAt: new Date(raw.created_at),
        updatedAt: new Date(raw.updated_at),
    };
}

function mapCampaignDetail(raw: RawCampaignDetail): Campaign {
    return {
        id: raw.id,
        name: raw.name,
        agentName: raw.agent_name,
        useCase: raw.use_case,
        status: raw.status,
        language: raw.language,
        timezone: raw.timezone,
        maxDurationSeconds: raw.max_duration_seconds,
        openingScript: raw.opening_script || "",
        closingScript: raw.closing_script || "",
        coreQuestions: raw.core_questions || [],
        conditionalLogic: raw.conditional_logic || {},
        triggerMethod: raw.trigger_method,
        retryAttempts: raw.retry_attempts,
        retryDelayMinutes: raw.retry_delay_minutes,
        webhookUrl: raw.webhook_url,
        apiEndpoint: raw.api_endpoint,
        scheduleConfig: { timingMode: "immediate", ...(raw.schedule_config || {}) },
        voiceAgentId: raw.voice_agent_id,
        voiceId: raw.voice_id || undefined,
        conversationMode: raw.conversation_mode || "scripted",
        objective: raw.objective || "",
        knowledgeDocuments: (raw.knowledge_documents || []).map((d: RawKnowledgeDocument) => ({
            id: d.id,
            fileName: d.file_name,
            fileSize: d.file_size,
            uploadedAt: d.uploaded_at,
        })),
        conversationGoal: raw.conversation_goal || "",
        topicAreas: raw.topic_areas || [],
        probingDepth: raw.probing_depth || "medium",
        guardrails: raw.guardrails || "",
        evaluationCriteria: (raw.evaluation_criteria || []).map((c: RawEvaluationCriterion) => ({
            id: c.id,
            name: c.name,
            description: c.description || "",
            weight: c.weight,
            enabled: c.enabled,
            order: c.order || 0,
        })),
        contactCount: raw.contact_count || 0,
        sessionCount: raw.session_count || 0,
        recentActivity: (raw.recent_activity || []).map(mapActivityLogEntry),
        createdAt: new Date(raw.created_at),
        updatedAt: new Date(raw.updated_at),
        activatedAt: raw.activated_at ? new Date(raw.activated_at) : undefined,
    };
}

function mapContact(raw: RawContact): CampaignContact {
    return {
        id: raw.id,
        phoneNumber: raw.phone_number,
        name: raw.name,
        merchantName: raw.merchant_name,
        extraData: raw.extra_data,
        status: raw.status,
        addedAt: new Date(raw.added_at),
        calledAt: raw.called_at ? new Date(raw.called_at) : undefined,
    };
}

function mapCallSessionListItem(raw: RawCallSession): CallSession {
    return {
        id: raw.id,
        contactName: raw.contact_name,
        contactPhone: raw.contact_phone,
        campaignId: raw.campaign_id || "",
        campaignName: raw.campaign_name || "",
        isTestCall: raw.is_test === true || !raw.campaign_id,
        status: raw.status,
        duration: raw.duration,
        score: raw.score || 0,
        sentiment: raw.sentiment || "neutral",
        category: raw.category,
        timestamp: new Date(raw.created_at),
        startedAt: new Date(raw.started_at),
        endedAt: raw.ended_at ? new Date(raw.ended_at) : undefined,
        scheduledAt: raw.scheduled_at ? new Date(raw.scheduled_at) : undefined,
        summary: raw.summary || "",
        transcript: [],
        scoreBreakdown: [],
        keyFindings: [],
        riskSignals: [],
        messageCount: raw.message_count ?? (raw.messages || raw.transcript || []).length ?? 0,
        retryCount: raw.retry_count ?? 0,
        failReason: raw.fail_reason,
        failCode: raw.fail_code,
        maxRetryAttempts: raw.max_retry_attempts,
    };
}

function mapCallSessionDetail(raw: RawCallSession): CallSession {
    return {
        id: raw.id,
        contactName: raw.contact_name,
        contactPhone: raw.contact_phone,
        campaignId: raw.campaign_id || "",
        campaignName: "",
        batchCallId: raw.batch_call_id,
        status: raw.status,
        duration: raw.duration,
        score: raw.score || 0,
        sentiment: raw.sentiment || "neutral",
        category: raw.category,
        timestamp: new Date(raw.created_at),
        startedAt: new Date(raw.started_at),
        endedAt: raw.ended_at ? new Date(raw.ended_at) : undefined,
        scheduledAt: raw.scheduled_at ? new Date(raw.scheduled_at) : undefined,
        summary: raw.summary || "",
        transcript: (raw.transcript || raw.messages || []).map((t: RawTranscriptEntry, i: number): TranscriptEntry => ({
            id: t.id || `msg-${i}`,
            speaker: t.speaker || (t.role === "assistant" ? "agent" : "user"),
            content: t.content || t.text || "",
            timestamp: t.timestamp_secs || t.timestamp || 0,
            order: t.order ?? i,
        })),
        scoreBreakdown: (raw.score_breakdowns || []).map((s: RawScoreBreakdown): ScoreBreakdown => ({
            criterion: s.criterion,
            score: s.score,
            maxScore: s.max_score,
            weight: s.weight,
            notes: s.notes,
        })),
        keyFindings: raw.key_findings || [],
        riskSignals: raw.risk_signals || [],
        nextActions: raw.next_actions
            ? { actions: raw.next_actions.map((a) => ({ action: a, priority: "medium" as const })) }
            : undefined,
        audioUrl: raw.audio_url,
        messageCount: raw.message_count ?? (raw.transcript || raw.messages || []).length ?? 0,
        retryCount: raw.retry_count ?? 0,
        failReason: raw.fail_reason,
        failCode: raw.fail_code,
        maxRetryAttempts: raw.max_retry_attempts,
        isTestCall: raw.is_test === true || !raw.campaign_id,
    };
}

function mapActivityLogEntry(raw: RawActivityLogEntry): ActivityLogEntry {
    return {
        id: raw.id,
        campaignId: raw.campaign_id || "",
        eventType: raw.event_type,
        message: raw.message,
        details: raw.details,
        contactPhone: raw.contact_phone,
        contactName: raw.contact_name,
        sessionId: raw.session_id,
        timestamp: new Date(raw.timestamp),
    };
}
